"use strict";
Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
const webStorage = require("./web-storage-1mQqDY5f.js");
const react = require("react");
const tryInitStorage = (accessType, key, defaultValue) => {
  if (typeof window !== "undefined") {
    const init = webStorage.webStorage.get({ accessType, key, returnString: false });
    if (typeof init === "string") {
      try {
        const parsedInit = JSON.parse(init);
        return parsedInit;
      } catch (e) {
        return init;
      }
    }
    if ((init === null || init === void 0) && defaultValue !== void 0) {
      webStorage.webStorage.set({ accessType, key, value: defaultValue });
      return defaultValue;
    }
    return init;
  }
  return void 0;
};
const getHookId = () => {
  var _a, _b, _c;
  if (typeof window !== "undefined" && window.crypto !== void 0 && ((_a = window.crypto) == null ? void 0 : _a.randomUUID) !== void 0) {
    return (_c = (_b = window.crypto) == null ? void 0 : _b.randomUUID()) != null ? _c : Date.now().toString();
  }
  return Date.now().toString();
};
const useWebStorage = (accessType, key, defaultValue = void 0) => {
  const [storageValue, setStorageValue] = react.useState(tryInitStorage(accessType, key, defaultValue));
  const isMounted = react.useRef(false);
  const hookId = react.useRef(getHookId());
  const lifeCycleEventKey = `thdStorageUtils.${accessType === webStorage.LOCAL_STORAGE ? "localStorage" : "sessionStorage"}`;
  react.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  react.useEffect(() => {
    const val = webStorage.webStorage.get({ accessType, key, returnString: false });
    setStorageValue(val);
  }, [accessType, key]);
  react.useEffect(() => {
    if (typeof window === "undefined" || typeof window.LIFE_CYCLE_EVENT_BUS === "undefined") {
      return () => {
      };
    }
    const eventHandler = ({ output }) => {
      const { key: eventStorageKey, hookId: eventHookId } = output != null ? output : {};
      if (eventHookId !== hookId.current && eventStorageKey === key) {
        const val = webStorage.webStorage.get({ accessType, key, returnString: false });
        if (isMounted.current) {
          setStorageValue(val);
        }
      }
    };
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on(lifeCycleEventKey, eventHandler);
    return () => {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.off(lifeCycleEventKey, eventHandler);
    };
  }, [accessType, key, setStorageValue]);
  const wrappedSetStorageValue = (value) => {
    if (value === null || value === void 0) {
      webStorage.webStorage.remove({ accessType, key });
      setStorageValue(void 0);
    } else {
      webStorage.webStorage.set({ accessType, key, value });
      const newValue = webStorage.webStorage.get({ accessType, key, returnString: false });
      setStorageValue(newValue);
    }
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(lifeCycleEventKey, {
      hookId: hookId.current,
      key
    });
  };
  return [storageValue, wrappedSetStorageValue];
};
/**
 * @preserve
 * @public
 * @function useLocalStorage
 * @description React hook that allows access to local storage values,
 *  the same way you would use a useState hook.
 * @param {string} key - Local Storage object key
 * @returns {[string | Object, (string | object) => void]}
 *  - Returns the value as a string or object, and a function to update the value
 */
const useLocalStorage = (key, defaultValue = void 0) => {
  return useWebStorage(webStorage.LOCAL_STORAGE, key, defaultValue);
};
/**
 * @preserve
 * @public
 * @function useSessionStorage
 * @description React hook that allows access to session storage values,
 *  the same way you would use a useState hook.
 * @param {string} key - Local Storage object key
 * @returns {[string | Object, (string | object) => void]}
 *  - Returns the value as a string or object, and a function to update the value
 */
const useSessionStorage = (key, defaultValue = void 0) => {
  return useWebStorage(webStorage.SESSION_STORAGE, key, defaultValue);
};
exports.useLocalStorage = useLocalStorage;
exports.useSessionStorage = useSessionStorage;
